import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Shield, Clock, AlertTriangle, KeyRound, CheckCircle2, Copy, Check, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = '';

/**
 * VerifyTotpPage — TOTP step-up after Google OAuth (admin branch).
 *
 * Reached via /verify-totp?pre_session_id=...&setup=1|0&email=...
 * after AuthCallbackPage detects {requires_2fa: true} from /api/auth/session.
 *
 * Two render modes:
 *   - setup=1 → fresh admin: show QR + manual key + 8 recovery codes,
 *     ask the user to type their first TOTP code; on success
 *     POST /api/auth/totp-setup-complete
 *   - setup=0 → existing admin: TOTP input only, plus the
 *     "Bloqué par erreur ?" recovery-code unlock affordance
 *     (re-purposed from this week's Path 1 fix); on success
 *     POST /api/auth/verify-totp
 */
export default function VerifyTotpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();

  const params = new URLSearchParams(location.search);
  const preSessionId = params.get('pre_session_id') || '';
  const initialIsSetup = params.get('setup') === '1';
  const emailMasked = params.get('email') || '';

  const [mode, setMode] = useState(initialIsSetup ? 'setup' : 'verify');
  const [setupData, setSetupData] = useState(null); // {totp_qr, totp_manual_key, recovery_codes}
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [savedAcknowledged, setSavedAcknowledged] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  // Lockout state (mirrors AdminAuth.js)
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Unlock-IP form
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState('');
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [unlockSuccess, setUnlockSuccess] = useState('');

  useEffect(() => {
    if (!preSessionId) {
      navigate('/', { replace: true });
      return;
    }
    // Fetch totp-status to confirm mode + grab QR/codes if setup
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/totp-status?pre_session_id=${encodeURIComponent(preSessionId)}`);
        if (res.status === 410) {
          setError('Session expirée. Reconnecte-toi via Google.');
          setLoading(false);
          return;
        }
        if (!res.ok) {
          setError('Erreur de connexion au serveur. Réessaie.');
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (data.state === 'needs_setup') {
          setMode('setup');
          setSetupData({
            totp_qr: data.totp_qr,
            totp_manual_key: data.totp_manual_key,
            recovery_codes: data.recovery_codes || [],
          });
        } else {
          setMode('verify');
        }
      } catch (e) {
        setError('Erreur de connexion au serveur. Réessaie.');
      } finally {
        setLoading(false);
      }
    })();
  }, [preSessionId, navigate]);

  // Lockout countdown
  useEffect(() => {
    if (!lockoutUntil) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= lockoutUntil) setLockoutUntil(null);
    }, 1000);
    return () => clearInterval(id);
  }, [lockoutUntil]);

  const isLocked = lockoutUntil && now < lockoutUntil;
  const lockoutInfo = (() => {
    if (!isLocked) return null;
    const remainingSec = Math.max(0, Math.ceil((lockoutUntil - now) / 1000));
    const minutes = Math.ceil(remainingSec / 60);
    const endDate = new Date(lockoutUntil);
    const hh = String(endDate.getHours()).padStart(2, '0');
    const mm = String(endDate.getMinutes()).padStart(2, '0');
    return { minutes, timeLabel: `${hh}:${mm}` };
  })();

  const parseLockout = (detail) => {
    const minMatch = String(detail || '').match(/(\d+)\s*min/i);
    const secMatch = String(detail || '').match(/(\d+)\s*s\b/i);
    if (minMatch) return parseInt(minMatch[1], 10) * 60;
    if (secMatch) return parseInt(secMatch[1], 10);
    return 1800;
  };

  const submitVerify = async () => {
    if (isLocked) return;
    if (!code.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const url = mode === 'setup' ? `${API_URL}/api/auth/totp-setup-complete` : `${API_URL}/api/auth/verify-totp`;
      const body = mode === 'setup'
        ? { pre_session_id: preSessionId, code: code.trim() }
        : { pre_session_id: preSessionId, code: code.trim(), is_recovery_code: useRecovery };
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}
      if (res.ok && data.user) {
        updateUser(data.user);
        navigate('/bloom-gestion-secure', { replace: true });
      } else if (res.status === 429) {
        setLockoutUntil(Date.now() + parseLockout(data.detail) * 1000);
        setNow(Date.now());
        setError('');
      } else if (res.status === 410) {
        setError('Session expirée. Reconnecte-toi via Google.');
      } else {
        setError(data.detail || 'Code invalide');
      }
    } catch (e) {
      setError('Erreur de connexion au serveur. Réessaie.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitUnlock = async () => {
    if (!unlockEmail.trim() || !unlockCode.trim()) return;
    setUnlockLoading(true);
    setUnlockError('');
    setUnlockSuccess('');
    try {
      const res = await fetch(`${API_URL}/api/auth/unlock-ip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: unlockEmail.trim(), recovery_code: unlockCode.trim().toUpperCase() }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}
      if (res.ok && data.unblocked) {
        setLockoutUntil(null);
        setError('');
        let msg = data.message || 'IP débloquée. Tu peux maintenant valider ton TOTP.';
        if (data.warning) msg += ' ' + data.warning;
        setUnlockSuccess(msg);
        setUnlockCode('');
        setTimeout(() => { setShowUnlock(false); setUnlockSuccess(''); }, 4000);
      } else if (res.status === 429) {
        setUnlockError(String(data.detail || 'Trop de tentatives. Réessaie plus tard.'));
      } else {
        setUnlockError(data.detail || 'Code invalide');
      }
    } catch (e) {
      setUnlockError('Erreur de connexion au serveur. Réessaie.');
    } finally {
      setUnlockLoading(false);
    }
  };

  const copyManualKey = async () => {
    if (!setupData?.totp_manual_key) return;
    try {
      await navigator.clipboard.writeText(setupData.totp_manual_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (_) {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1514] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1514] flex items-center justify-center p-4" data-testid="verify-totp-page">
      <div className="w-full max-w-md bg-white" data-testid="verify-totp-card">
        <div className="bg-[#1A1514] text-white px-6 py-5">
          <div className="flex items-center gap-3">
            <Shield size={20} className="text-[#D4AF37]" />
            <h1 className="text-lg font-medium">{mode === 'setup' ? 'Configuration 2FA — première connexion' : 'Vérification 2FA'}</h1>
          </div>
          {emailMasked && <p className="text-xs text-[#A8A6A2] mt-1" data-testid="verify-totp-email">{emailMasked}</p>}
        </div>

        <div className="p-6 space-y-4">
          {isLocked && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-900 text-sm p-3" data-testid="totp-lockout">
              <Clock size={18} className="mt-0.5 flex-shrink-0 text-amber-700" />
              <div className="space-y-1">
                <p className="font-medium">Connexion temporairement bloquée</p>
                <p className="text-amber-800">
                  Trop de tentatives échouées. Réessaie à <span className="font-semibold" data-testid="totp-lockout-time">{lockoutInfo.timeLabel}</span> (dans {lockoutInfo.minutes} min).
                </p>
              </div>
            </div>
          )}

          {isLocked && !showUnlock && !unlockSuccess && (
            <button
              type="button"
              onClick={() => { setShowUnlock(true); setUnlockError(''); }}
              className="w-full text-left text-xs text-amber-900 underline hover:text-amber-950 -mt-2"
              data-testid="totp-unlock-toggle-btn"
            >
              Bloqué par erreur ? Débloquer avec un code de récupération
            </button>
          )}

          {isLocked && showUnlock && !unlockSuccess && (
            <div className="border border-amber-300 bg-amber-50/50 p-4 space-y-3" data-testid="totp-unlock-form">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                <KeyRound size={16} /> Débloquer cette IP
              </div>
              {unlockError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs p-2" data-testid="totp-unlock-error">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {unlockError}
                </div>
              )}
              <input type="email" value={unlockEmail} onChange={e => setUnlockEmail(e.target.value)} disabled={unlockLoading} placeholder="email admin" className="w-full border border-amber-300 bg-white px-3 py-2 text-sm focus:outline-none disabled:opacity-60" data-testid="totp-unlock-email" />
              <input type="text" value={unlockCode} onChange={e => setUnlockCode(e.target.value.toUpperCase())} disabled={unlockLoading} placeholder="XXXXXX-XXXXXX" className="w-full border border-amber-300 bg-white px-3 py-2 text-sm font-mono uppercase focus:outline-none disabled:opacity-60" data-testid="totp-unlock-code" maxLength={20} />
              <div className="flex gap-2">
                <button type="button" onClick={submitUnlock} disabled={unlockLoading || !unlockEmail.trim() || !unlockCode.trim()} className="flex-1 bg-amber-700 text-white py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="totp-unlock-submit-btn">
                  {unlockLoading ? <Loader2 size={14} className="animate-spin" /> : 'Débloquer cette IP'}
                </button>
                <button type="button" onClick={() => { setShowUnlock(false); setUnlockCode(''); }} className="px-3 text-xs text-amber-900" data-testid="totp-unlock-cancel-btn">Annuler</button>
              </div>
            </div>
          )}

          {unlockSuccess && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 text-green-700 text-sm p-3" data-testid="totp-unlock-success">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /><span>{unlockSuccess}</span>
            </div>
          )}

          {error && !isLocked && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="verify-totp-error">
              <AlertTriangle size={16} />{error}
            </div>
          )}

          {mode === 'setup' && setupData && (
            <div className="space-y-4" data-testid="setup-block">
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <strong>Étape 1 — Scanne le QR.</strong> Ouvre Google Authenticator (ou équivalent) et scanne le code ci-dessous.
              </div>
              <div className="flex justify-center">
                <img src={setupData.totp_qr} alt="QR TOTP" className="w-48 h-48" data-testid="totp-qr" />
              </div>
              <div>
                <p className="text-xs text-[#6B6A68] mb-1">Ou saisis cette clé manuellement :</p>
                <div className="flex items-center gap-2 bg-[#F5F4F2] border border-[#E0DFDD] px-3 py-2 font-mono text-xs">
                  <Smartphone size={14} className="text-[#6B6A68]" />
                  <span className="flex-1 break-all" data-testid="totp-manual-key">{setupData.totp_manual_key}</span>
                  <button type="button" onClick={copyManualKey} className="text-[#6B6A68] hover:text-[#1A1514]" data-testid="totp-copy-key">
                    {copiedKey ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <strong>Étape 2 — Sauvegarde tes 8 codes de récupération.</strong> Ils ne seront <em>plus jamais</em> affichés. Range-les dans un gestionnaire de mots de passe.
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="recovery-codes-grid">
                {setupData.recovery_codes.map((c, i) => (
                  <div key={i} className="font-mono text-xs text-center bg-[#F9F8F6] border border-[#E0DFDD] py-2" data-testid={`recovery-code-${i}`}>{c}</div>
                ))}
              </div>
              <label className="flex items-start gap-2 text-xs text-[#1A1514] cursor-pointer">
                <input type="checkbox" checked={savedAcknowledged} onChange={e => setSavedAcknowledged(e.target.checked)} data-testid="setup-ack" />
                <span>J'ai sauvegardé mes 8 codes de récupération dans un endroit sûr.</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <strong>Étape 3 — Confirme l'appairage.</strong> Tape le code à 6 chiffres affiché par ton application.
              </div>
            </div>
          )}

          {mode === 'verify' && !useRecovery && (
            <p className="text-sm text-[#6B6A68]" data-testid="verify-totp-prompt">
              Saisis le code à 6 chiffres affiché par ton application d'authentification.
            </p>
          )}
          {mode === 'verify' && useRecovery && (
            <p className="text-sm text-[#6B6A68]" data-testid="verify-totp-recovery-prompt">
              Saisis l'un de tes 8 codes de récupération (usage unique).
            </p>
          )}

          <input
            type="text"
            value={code}
            onChange={e => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={isLocked || submitting}
            placeholder={useRecovery ? 'XXXXXX-XXXXXX' : '123456'}
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoFocus
            className="w-full border border-[#E0DFDD] px-3 py-3 text-center font-mono tracking-widest text-lg focus:outline-none focus:border-[#1A1514] disabled:bg-[#F5F4F2] disabled:opacity-60"
            data-testid="verify-totp-input"
            maxLength={useRecovery ? 20 : 6}
          />

          <button
            type="button"
            onClick={submitVerify}
            disabled={isLocked || submitting || !code.trim() || (mode === 'setup' && !savedAcknowledged)}
            className="w-full bg-[#1A1514] text-white py-3 text-sm font-medium uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="verify-totp-submit"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : (mode === 'setup' ? 'Confirmer & terminer' : 'Vérifier')}
          </button>

          {mode === 'verify' && !isLocked && (
            <button
              type="button"
              onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError(''); }}
              className="w-full text-xs text-[#6B6A68] hover:text-[#1A1514] underline"
              data-testid="verify-totp-toggle-recovery"
            >
              {useRecovery ? '← Revenir au code TOTP' : 'Utiliser un code de récupération'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
